/*
 * Alaya Junior — PDP cross-sell module (Agenda 10)
 *
 * Two pieces:
 *   <alaya-pairings>  fills itself from Shopify's recommendations endpoint,
 *                     trying complementary products first and falling back to
 *                     the algorithmic set. If both come back empty the whole
 *                     section is removed rather than leaving an empty heading —
 *                     the "no placeholders, ever" rule from Agenda 7.
 *   AlayaQuickView    a single left-hand drawer, shared by every card, that
 *                     shows the product large and unframed with its sizes,
 *                     copy and an add-to-cart. Content comes from
 *                     sections/pairing-quick-view.liquid, fetched standalone
 *                     the way pickup-availability.js fetches its section.
 *
 * Add-to-cart reuses the exact path product-form.js uses, so the existing cart
 * drawer opens with no changes to cart-drawer.liquid.
 */

const PAIRING_QV_SECTION = 'pairing-quick-view';

class AlayaQuickView {
  constructor() {
    this.root = null;
    this.panel = null;
    this.body = null;
    this.opener = null;
    this.cache = new Map();
    this.onKeydown = this.onKeydown.bind(this);
  }

  /** Build the shell once, lazily — nothing is added to the DOM until first use. */
  ensure() {
    if (this.root) return;

    this.root = document.createElement('div');
    this.root.className = 'pqv-drawer';
    this.root.setAttribute('hidden', '');
    this.root.innerHTML = `
      <div class="pqv-drawer__overlay" data-pqv-overlay></div>
      <div class="pqv-drawer__panel" role="dialog" aria-modal="true" aria-label="Product quick view">
        <div class="pqv-drawer__body" data-pqv-body></div>
      </div>
    `;
    document.body.appendChild(this.root);

    this.panel = this.root.querySelector('.pqv-drawer__panel');
    this.body = this.root.querySelector('[data-pqv-body]');

    this.root.querySelector('[data-pqv-overlay]').addEventListener('click', () => this.close());
    this.root.addEventListener('click', (event) => {
      if (event.target.closest('[data-pqv-close]')) {
        this.close();
        return;
      }
      const pill = event.target.closest('[data-pqv-option-value]');
      if (pill) {
        this.selectOptionValue(pill);
        return;
      }
      const add = event.target.closest('[data-pqv-add]');
      if (add) this.addToCart(add);
    });
  }

  async open(productUrl, opener) {
    this.ensure();
    this.opener = opener ?? null;

    this.root.removeAttribute('hidden');
    // Next frame, so the transition runs from the closed position rather than
    // being collapsed into the same style recalculation.
    requestAnimationFrame(() => this.root.classList.add('is-open'));
    document.body.classList.add('pqv-open');
    document.addEventListener('keydown', this.onKeydown);

    this.body.innerHTML = '<div class="pqv__loading" role="status">Loading…</div>';

    const html = await this.fetchContent(productUrl);
    if (html === null) {
      // Never strand the shopper in an empty drawer — send them to the product.
      window.location.href = productUrl;
      return;
    }
    this.body.innerHTML = html;
    this.panel.scrollTop = 0;
    // Compute reachability and the add state for the pre-selected combination,
    // so an initially sold-out size is greyed before the shopper touches it.
    this.refresh();
    this.body.querySelector('[data-pqv-close]')?.focus();
  }

  async fetchContent(productUrl) {
    if (this.cache.has(productUrl)) return this.cache.get(productUrl);
    try {
      const separator = productUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${productUrl}${separator}section_id=${PAIRING_QV_SECTION}`);
      if (!response.ok) return null;

      const holder = document.createElement('div');
      holder.innerHTML = await response.text();
      const content = holder.querySelector('[data-pqv-root]');
      if (!content) return null;

      this.cache.set(productUrl, content.outerHTML);
      return content.outerHTML;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  close() {
    if (!this.root) return;
    this.root.classList.remove('is-open');
    document.body.classList.remove('pqv-open');
    document.removeEventListener('keydown', this.onKeydown);

    const finish = () => {
      this.root.setAttribute('hidden', '');
      this.body.innerHTML = '';
    };
    // Wait for the slide-out, but never leave the drawer stuck open if the
    // transition never fires (reduced motion, background tab).
    const panel = this.panel;
    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      panel.removeEventListener('transitionend', once);
      finish();
    };
    panel.addEventListener('transitionend', once);
    setTimeout(once, 400);

    this.opener?.focus();
    this.opener = null;
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  /** The variant table rendered alongside the pills, or [] if single-variant. */
  variants() {
    const node = this.body.querySelector('[data-pqv-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /** The currently selected value for each option, in option order. */
  selection() {
    const chosen = [];
    for (const pill of this.body.querySelectorAll('[data-pqv-option-value].is-selected')) {
      chosen[Number(pill.dataset.optionIndex)] = pill.dataset.value;
    }
    return chosen;
  }

  selectOptionValue(pill) {
    const index = Number(pill.dataset.optionIndex);

    for (const other of this.body.querySelectorAll(`[data-pqv-option-value][data-option-index="${index}"]`)) {
      const selected = other === pill;
      other.classList.toggle('is-selected', selected);
      other.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    this.refresh();
  }

  /**
   * Resolve the current option selection to a variant, then update the price,
   * the add button, and which values are still reachable.
   */
  refresh() {
    const variants = this.variants();
    if (variants.length === 0) return;

    const chosen = this.selection();
    const match = variants.find((v) => v.options.every((value, i) => value === chosen[i]));

    // Grey out values that cannot be had alongside the OTHER current choices,
    // rather than values that are globally sold out — otherwise switching
    // design would leave every size looking dead.
    for (const pill of this.body.querySelectorAll('[data-pqv-option-value]')) {
      const index = Number(pill.dataset.optionIndex);
      const reachable = variants.some(
        (v) =>
          v.available &&
          v.options[index] === pill.dataset.value &&
          v.options.every((value, i) => i === index || chosen[i] === undefined || value === chosen[i])
      );
      pill.classList.toggle('is-unavailable', !reachable);
    }

    const price = this.body.querySelector('[data-pqv-price]');
    if (price && match) price.textContent = match.price;

    const add = this.body.querySelector('[data-pqv-add]');
    if (!add) return;
    const label = add.querySelector('[data-pqv-add-label]');

    if (match && match.available) {
      add.dataset.variantId = match.id;
      add.disabled = false;
      if (label) label.textContent = 'Add to cart';
    } else {
      add.disabled = true;
      if (label) label.textContent = match ? 'Sold out' : 'Unavailable';
    }
  }

  addToCart(button) {
    if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;
    button.setAttribute('aria-disabled', 'true');
    const label = button.querySelector('[data-pqv-add-label]');
    const previous = label?.textContent;
    if (label) label.textContent = 'Adding…';

    addVariantToCart(button.dataset.variantId)
      .then((response) => {
        if (!response) {
          if (label) label.textContent = previous ?? 'Add to cart';
          return;
        }
        // Close first so the cart drawer is not opening behind this one.
        this.close();
      })
      .finally(() => {
        button.removeAttribute('aria-disabled');
        setTimeout(() => {
          if (label) label.textContent = previous ?? 'Add to cart';
        }, 600);
      });
  }
}

const alayaQuickView = new AlayaQuickView();

/**
 * Shared cart add. Mirrors product-form.js so the cart drawer, its section
 * rendering and the cart bubble all update exactly as they do elsewhere.
 * Resolves with the cart response, or null when the add failed.
 */
function addVariantToCart(variantId) {
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);

  if (cart) {
    formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    cart.setActiveElement(document.activeElement);
  }

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];
  config.body = formData;

  return fetch(routes.cart_add_url, config)
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        console.error(response.description || response.message);
        return null;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-pairings',
        productVariantId: variantId,
        cartData: response,
      });

      if (cart) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(response); // opens the cart drawer itself
      }
      return response;
    })
    .catch((error) => {
      console.error(error);
      return null;
    });
}

class AlayaPairings extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;

    this.addEventListener('click', this.onClick.bind(this));

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    const preferred = this.dataset.intent === 'complementary' ? 'complementary' : 'related';

    let filled = await this.fetchInto(preferred);
    if (!filled && preferred === 'complementary') {
      filled = await this.fetchInto('related');
    }

    if (!filled) {
      (this.closest('.shopify-section') ?? this).remove();
      return;
    }

    this.dataset.loaded = 'true';
  }

  async fetchInto(intent) {
    const url =
      `${this.dataset.url}&product_id=${this.dataset.productId}` +
      `&section_id=${this.dataset.sectionId}&intent=${intent}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return false;

      const holder = document.createElement('div');
      holder.innerHTML = await response.text();

      const fragment = holder.querySelector('alaya-pairings');
      if (!fragment || !fragment.querySelector('.pairings__item')) return false;

      this.innerHTML = fragment.innerHTML;
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  onClick(event) {
    const quickview = event.target.closest('[data-pairing-quickview]');
    if (!quickview) return;
    event.preventDefault();
    alayaQuickView.open(quickview.dataset.productUrl, quickview);
  }
}

if (!customElements.get('alaya-pairings')) {
  customElements.define('alaya-pairings', AlayaPairings);
}
